import { formatWholeNumber } from '../util/numberFormat';

// Retroalimentación pedagógica determinista para Suma en Modo Práctica.
// No cambia el banco ni genera ejercicios: solo explica el problema ya seleccionado.

const PLACE_NAMES = ['unidades', 'decenas', 'centenas', 'unidades de millar'];

const digitCount = value => Math.max(1, String(Math.abs(value)).length);

export function classifyAdditionProblem(problem) {
  const width = Math.max(digitCount(problem.a), digitCount(problem.b));
  let a = problem.a;
  let b = problem.b;
  let carry = 0;
  let hasRegrouping = false;

  while (a > 0 || b > 0) {
    const total = (a % 10) + (b % 10) + carry;
    carry = Math.floor(total / 10);
    if (carry > 0) hasRegrouping = true;
    a = Math.floor(a / 10);
    b = Math.floor(b / 10);
  }

  if (width === 1) return 'single';
  return `${width}_${hasRegrouping ? 'regroup' : 'no_regroup'}`;
}

export function getAdditionHint(problem) {
  const kind = classifyAdditionProblem(problem);
  const hints = {
    single: 'Empieza en el número mayor y cuenta hacia adelante.',
    '2_no_regroup': 'Recuerda alinear unidades con unidades y decenas con decenas.',
    '2_regroup': 'Empieza por las unidades. Si hay 10 o más, reagrupa.',
    '3_no_regroup': 'Alinea centenas, decenas y unidades.',
    '3_regroup': 'Suma de derecha a izquierda y reagrupa cuando obtengas 10 o más.',
  };
  return hints[kind] || 'Alinea los números por valor posicional y suma de derecha a izquierda.';
}

function buildColumns(a, b) {
  const operandWidth = Math.max(digitCount(a), digitCount(b));
  const result = a + b;
  const gridWidth = Math.max(operandWidth, digitCount(result));
  const columns = [];
  let carryIn = 0;

  for (let rightIndex = 0; rightIndex < operandWidth; rightIndex += 1) {
    const aDigit = Math.floor(a / (10 ** rightIndex)) % 10;
    const bDigit = Math.floor(b / (10 ** rightIndex)) % 10;
    const total = aDigit + bDigit + carryIn;
    const digit = total % 10;
    const carryOut = Math.floor(total / 10);
    columns.push({
      rightIndex,
      gridColumn: gridWidth - 1 - rightIndex,
      place: PLACE_NAMES[rightIndex] || `posición ${rightIndex + 1}`,
      nextPlace: PLACE_NAMES[rightIndex + 1] || `posición ${rightIndex + 2}`,
      aDigit,
      bDigit,
      carryIn,
      total,
      digit,
      carryOut,
    });
    carryIn = carryOut;
  }

  return { operandWidth, gridWidth, columns, result };
}

function buildSingleDigitTutorial(problem) {
  // La recta conserva el orden escrito de la suma: primer sumando + segundo sumando.
  // Así no se introduce la propiedad conmutativa de manera implícita.
  const start = problem.a;
  const stepsToCount = problem.b;
  const result = problem.answer;
  const jumpLabel = stepsToCount === 1 ? 'salto' : 'saltos';

  return {
    kind: 'number-line',
    a: problem.a,
    b: problem.b,
    result,
    numberLineMax: 20,
    steps: [
      {
        id: 'start',
        label: 'Paso 1',
        title: 'Empieza en ' + formatWholeNumber(start) + '.',
        message: 'Comenzamos en el primer número: ' + formatWholeNumber(start) + '.',
        showJumps: false,
        showResult: false,
      },
      {
        id: 'count',
        label: 'Paso 2',
        title: stepsToCount === 0
          ? 'No damos ningún salto porque sumamos 0.'
          : 'Damos ' + formatWholeNumber(stepsToCount) + ' ' + jumpLabel + ' a la derecha desde el ' + formatWholeNumber(start) + '.',
        message: '',
        showJumps: true,
        showResult: false,
      },
      {
        id: 'result',
        label: 'Resultado',
        title: 'Llegamos al ' + formatWholeNumber(result) + '.',
        message: 'Entonces, ' + formatWholeNumber(problem.a) + ' + ' + formatWholeNumber(problem.b) + ' = ' + formatWholeNumber(result) + '.',
        showJumps: true,
        showResult: true,
        result,
      },
    ],
  };
}

function buildColumnTutorial(problem) {
  const calculation = buildColumns(problem.a, problem.b);
  const kind = classifyAdditionProblem(problem);
  const steps = [{
    id: 'align',
    label: 'Paso 1',
    title: 'Alinea los números por valor posicional.',
    message: calculation.gridWidth === 2
      ? 'Las decenas quedan debajo de las decenas y las unidades debajo de las unidades.'
      : 'Las centenas, decenas y unidades deben quedar en la misma columna.',
    activeColumn: null,
    resultVisible: [],
    carries: [],
  }];

  calculation.columns.forEach((column, index) => {
    const hasRegrouping = column.carryOut > 0;
    const expression = column.carryIn
      ? `${column.aDigit} + ${column.bDigit} + ${column.carryIn} = ${column.total}`
      : `${column.aDigit} + ${column.bDigit} = ${column.total}`;
    const writeMessage = hasRegrouping
      ? `Escribe ${column.digit} en las ${column.place} y reagrupa ${column.carryOut} ${column.nextPlace}.`
      : `Escribe ${column.digit} en las ${column.place}.`;

    steps.push({
      id: `column-${column.rightIndex}`,
      label: `Paso ${index + 2}`,
      title: `Suma las ${column.place}.`,
      message: `${expression}. ${writeMessage}`,
      activeColumn: column.gridColumn,
      resultVisible: calculation.columns.slice(0, index + 1).map(item => item.gridColumn),
      carries: calculation.columns
        .slice(0, index + 1)
        .filter(item => item.carryOut > 0 && item.gridColumn > 0)
        .map(item => ({ gridColumn: item.gridColumn - 1, value: item.carryOut })),
    });
  });

  steps.push({
    id: 'result',
    label: 'Resultado',
    title: `${formatWholeNumber(problem.a)} + ${formatWholeNumber(problem.b)} = ${formatWholeNumber(problem.answer)}`,
    message: kind.endsWith('_regroup')
      ? 'El resultado conserva el valor posicional de cada columna.'
      : 'Ya sumaste cada columna por valor posicional.',
    activeColumn: null,
    resultVisible: Array.from({ length: calculation.gridWidth }, (_, index) => index),
    carries: [],
    result: problem.answer,
  });

  return {
    kind: 'columns',
    a: problem.a,
    b: problem.b,
    result: problem.answer,
    gridWidth: calculation.gridWidth,
    steps,
  };
}

export function buildAdditionWorkedExample(problem) {
  return classifyAdditionProblem(problem) === 'single'
    ? buildSingleDigitTutorial(problem)
    : buildColumnTutorial(problem);
}
